import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from './LanguageContext';
import { apiGet, apiDelete, apiPut } from '../utils/api';
import UserForm from './UserForm';
import Pagination from './Pagination';
import useIsMobile from '../hooks/useIsMobile';

const Users = ({ admin }) => {
  const { print_lang } = useLanguage();
  const isMobile = useIsMobile();
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showResetPassword, setShowResetPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [filterBranch, setFilterBranch] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchData = useCallback(async () => {
    try {
      const [usersData, branchesData] = await Promise.all([
        apiGet(`/api/admin/users${filterBranch ? `?branch_id=${filterBranch}` : ''}`),
        apiGet('/api/admin/branches'),
      ]);

      if (usersData.success) {
        setUsers(usersData.data);
      }
      if (branchesData.success) {
        setBranches(branchesData.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [filterBranch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterBranch]);

  const handleAdd = () => {
    setEditingUser(null);
    setShowModal(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setShowModal(true);
  };

  const handleDelete = async (user) => {
    if (!window.confirm(print_lang('common.confirmDelete', 'Are you sure you want to delete?'))) {
      return;
    }

    try {
      const data = await apiDelete(`/api/admin/users/${user.user_id}`);
      if (data.success) {
        fetchData();
      } else {
        alert(data.message || 'Delete failed');
      }
    } catch (err) {
      alert(err.message || 'Failed to delete user');
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }

    try {
      const data = await apiPut(`/api/admin/users/${showResetPassword.user_id}/reset-password`, {
        new_password: newPassword,
      });
      if (data.success) {
        alert('Password reset successfully');
        setShowResetPassword(null);
        setNewPassword('');
      } else {
        alert(data.message || 'Reset failed');
      }
    } catch (err) {
      alert(err.message || 'Failed to reset password');
    }
  };

  const handleSave = () => {
    setShowModal(false);
    fetchData();
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  // Filter users by search term
  const filteredUsers = users.filter(user => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      user.name?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search)
    );
  });

  // Paginate
  const totalCount = filteredUsers.length;
  const totalPages = Math.ceil(totalCount / pageSize);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner" />
      </div>
    );
  }

  const actionButtons = (user) => (
    <div className="action-buttons">
      <button className="btn btn-sm btn-outline" onClick={() => handleEdit(user)} title={print_lang('common.edit', 'Edit')}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
      </button>
      <button className="btn btn-sm btn-secondary" onClick={() => setShowResetPassword(user)} title={print_lang('user.resetPassword', 'Reset Password')}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      </button>
      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(user)} title={print_lang('common.delete', 'Delete')}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        </svg>
      </button>
    </div>
  );

  const roleBadge = (user) => (
    <span className={`badge ${user.role === 'admin' ? 'badge-violet' : 'badge-info'}`}>
      {user.role === 'admin' ? print_lang('user.roleAdmin', 'Admin') : print_lang('user.roleOfficer', 'Officer')}
    </span>
  );

  const statusBadge = (user) => (
    <span className={`badge ${user.status === 'active' ? 'badge-success' : 'badge-danger'}`}>
      {user.status === 'active' ? print_lang('common.active', 'Active') : print_lang('common.inactive', 'Inactive')}
    </span>
  );

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">{print_lang('nav.users', 'Users')}</h1>
        <button className="btn btn-primary" onClick={handleAdd}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          {print_lang('user.addUser', 'Add User')}
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Filters */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="grid-2">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">{print_lang('common.search', 'Search')}</label>
            <input
              type="text"
              className="form-input"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">{print_lang('common.filter', 'Filter')} {print_lang('user.branch', 'Branch')}</label>
            <select
              className="form-select"
              value={filterBranch}
              onChange={(e) => setFilterBranch(e.target.value)}
            >
              <option value="">{print_lang('common.all', 'All')}</option>
              {branches.map((branch) => (
                <option key={branch.branch_id} value={branch.branch_id}>
                  {branch.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="card">
        {paginatedUsers.length === 0 && !searchTerm && !filterBranch ? (
          <div className="empty-state">
            <svg className="empty-state-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            <h3 className="empty-state-title">{print_lang('common.noData', 'No data found')}</h3>
            <p className="empty-state-text">Get started by adding your first user</p>
            <button className="btn btn-primary" onClick={handleAdd}>
              {print_lang('user.addUser', 'Add User')}
            </button>
          </div>
        ) : paginatedUsers.length === 0 ? (
          <div className="empty-state">
            <svg className="empty-state-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <h3 className="empty-state-title">{print_lang('common.noData', 'No data found')}</h3>
            <p className="empty-state-text">No users match your search</p>
          </div>
        ) : (
          <>
            {isMobile ? (
              <ul className="user-card-list">
                {paginatedUsers.map((user) => (
                  <li className="user-card" key={user.user_id}>
                    <div className="user-card-top">
                      <div className="user-card-identity">
                        <strong className="user-card-name">{user.name}</strong>
                        <span className="user-card-email">{user.email}</span>
                      </div>
                      {statusBadge(user)}
                    </div>
                    <div className="user-card-meta">
                      {roleBadge(user)}
                      <span className="user-card-branch">
                        <span className="user-card-branch-label">{print_lang('user.branch', 'Branch')}</span>
                        {user.branch_name || '-'}
                      </span>
                    </div>
                    <div className="user-card-actions">
                      <span className="user-card-actions-label">
                        {print_lang('user.actions', 'Actions')}
                      </span>
                      {actionButtons(user)}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>{print_lang('user.name', 'Name')}</th>
                      <th>{print_lang('user.email', 'Email')}</th>
                      <th>{print_lang('user.role', 'Role')}</th>
                      <th>{print_lang('user.branch', 'Branch')}</th>
                      <th>{print_lang('user.status', 'Status')}</th>
                      <th>{print_lang('common.actions', 'Actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedUsers.map((user) => (
                      <tr key={user.user_id}>
                        <td>
                          <strong>{user.name}</strong>
                        </td>
                        <td>{user.email}</td>
                        <td>{roleBadge(user)}</td>
                        <td>{user.branch_name || '-'}</td>
                        <td>{statusBadge(user)}</td>
                        <td>{actionButtons(user)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </>
        )}
      </div>

      {showModal && (
        <UserForm
          user={editingUser}
          branches={branches}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}

      {/* Reset Password Modal */}
      {showResetPassword && (
        <div className="modal-overlay" onClick={() => setShowResetPassword(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{print_lang('user.resetPassword', 'Reset Password')}</h2>
              <button className="modal-close" onClick={() => setShowResetPassword(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '1rem', color: '#6b7280' }}>
                Reset password for <strong>{showResetPassword.name}</strong> ({showResetPassword.email})
              </p>
              <div className="form-group">
                <label className="form-label">{print_lang('auth.newPassword', 'New Password')}</label>
                <input
                  type="password"
                  className="form-input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 6 characters"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowResetPassword(null)}>
                {print_lang('common.cancel', 'Cancel')}
              </button>
              <button className="btn btn-primary" onClick={handleResetPassword}>
                {print_lang('user.resetPassword', 'Reset Password')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
